import { useEffect, useState } from "react";
import { Presentation, FileDown, FileText, Mail, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, dt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ProjectSelect, Badge2, Empty } from "@/components/common";
import ShareButton from "@/components/ShareDialog";

export async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error("Download failed"); }
}

function EmailDialog({ pres, onClose }) {
  const [team, setTeam] = useState([]);
  const [f, setF] = useState({ user_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/team").then((r) => setTeam(r.data.filter((u) => u.active))); }, []);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/presentations/${pres.id}/email`, f);
      toast.success(`Email sent to ${r.data.to}`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-800" data-testid="email-presentation-dialog">
        <DialogHeader><DialogTitle>Email presentation</DialogTitle><DialogDescription>Emails a secure view-only link to a team member, who can forward it to investors. For anyone outside your team, copy the link with Share instead.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label className="text-xs">Team member</Label>
          <Select value={f.user_id} onValueChange={(v) => setF({ user_id: v })}><SelectTrigger data-testid="email-to-select"><SelectValue placeholder="Select recipient" /></SelectTrigger>
            <SelectContent>{team.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent></Select></div>
        <DialogFooter><Button onClick={send} disabled={busy || !f.user_id} data-testid="email-send-button">{busy ? "Sending..." : "Send email"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Presentations() {
  const { can } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [rows, setRows] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(null);
  const [email, setEmail] = useState(null);
  const load = () => api.get("/presentations").then((r) => setRows(r.data));
  useEffect(() => { api.get("/presentations/templates").then((r) => setTemplates(r.data)); load(); }, []);
  const gen = async (t) => {
    setBusy(t);
    try { await api.post("/presentations", { template: t, project_id: projectId || null }); toast.success("Presentation ready"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const del = async (id) => { await api.delete(`/presentations/${id}`); load(); };
  return (
    <div data-testid="presentations-page">
      <PageHeader label="Grow" title="Boardroom Presentation Generator" subtitle="One-click PowerPoint and PDF decks built from your live projects, campaigns, leads, bookings, revenue, forecasts and AI recommendations.">
        <ProjectSelect allowAll value={projectId} onChange={setProjectId} testid="presentation-project-filter" />
      </PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-10">
        {templates.map((t) => (
          <div key={t.id} className="lx-card flex flex-col" data-testid={`template-card-${t.id}`}>
            <Presentation className="h-5 w-5 text-cyan-400" />
            <div className="font-bold mt-3">{t.title}</div>
            <div className="text-sm text-slate-500 mt-1 flex-1">{t.description}</div>
            <div className="flex flex-wrap gap-1 mt-3">{t.sections.map((s) => <Badge2 key={s} tone="slate">{s.replace(/_/g, " ")}</Badge2>)}</div>
            <Button className="mt-4" onClick={() => gen(t.id)} disabled={!!busy} data-testid={`template-generate-${t.id}`}>{busy === t.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Generate deck</Button>
          </div>
        ))}
      </div>
      <div className="text-lg font-bold mb-4">Generated decks</div>
      {!rows.length && <Empty title="No decks yet">Pick a template above.</Empty>}
      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.id} className="lx-card flex flex-col lg:flex-row lg:items-center justify-between gap-3" data-testid={`presentation-row-${p.id}`}>
            <div><div className="font-semibold">{p.title}</div><div className="text-xs text-slate-500 font-mono">{p.created_by_name} · {dt(p.created_at)}</div></div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => download(`/presentations/${p.id}/pptx`, `LaunchX360-${p.template}.pptx`)} data-testid={`presentation-pptx-${p.id}`}><FileDown className="h-4 w-4 mr-2" />PowerPoint</Button>
              <Button variant="secondary" onClick={() => download(`/presentations/${p.id}/pdf`, `LaunchX360-${p.template}.pdf`)} data-testid={`presentation-pdf-${p.id}`}><FileText className="h-4 w-4 mr-2" />PDF</Button>
              <ShareButton kind="presentation" refId={p.id} />
              <Button variant="secondary" onClick={() => setEmail(p)} data-testid={`presentation-email-${p.id}`}><Mail className="h-4 w-4 mr-2" />Email</Button>
              {can("analytics", "write") && <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`presentation-delete-${p.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>}
            </div>
          </div>
        ))}
      </div>
      {email && <EmailDialog pres={email} onClose={() => setEmail(null)} />}
    </div>
  );
}
